import { useEffect, useState } from "react";
import { FileText, Image as ImageIcon, Mic, Search, Star, X } from "lucide-react";
import type { Message } from "./types";
import { Avatar, Empty, Spinner, useChat } from "./ui";
import { call } from "./api";
import JalaliPicker from "./JalaliPicker";
import { fmtDateTime } from "./jalali";

interface Props { roomId?: number | null; onClose: () => void }

function Hit({ text, q }: { text: string; q: string }) {
  if (!q) return <>{text}</>;
  const i = text.toLowerCase().indexOf(q.toLowerCase()); if (i < 0) return <>{text.slice(0, 140)}</>;
  const s = Math.max(0, i - 40);
  return <>{s > 0 ? "…" : ""}{text.slice(s, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length, i + q.length + 100)}</>;
}

export default function SearchPanel({ roomId, onClose }: Props) {
  const { rooms, users, roomById, openRoom } = useChat();
  const [q, setQ] = useState(""); const [room, setRoom] = useState<number>(roomId || 0);
  const [sender, setSender] = useState(0); const [type, setType] = useState("");
  const [from, setFrom] = useState<Date | null>(null); const [to, setTo] = useState<Date | null>(null);
  const [res, setRes] = useState<Message[] | null>(null); const [busy, setBusy] = useState(false); const [starred, setStarred] = useState(false);
  const hasFilter = q.trim().length >= 2 || !!sender || !!type || !!from || !!to;

  useEffect(() => {
    if (starred) { setBusy(true); call("stars").then(r => { setRes(r.success ? r.messages : []); setBusy(false); }); return; }
    if (!hasFilter) { setRes(null); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      const day0 = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
      const r = await call("search", { q: q.trim(), room_id: room || undefined, sender_id: sender || undefined, type: type || undefined,
        from: from ? day0(from).toISOString() : undefined, to: to ? new Date(day0(to).getTime() + 86400000).toISOString() : undefined });
      setRes(r.success ? r.messages ?? r.results : []); setBusy(false);
    }, 380);
    return () => clearTimeout(t);
  }, [q, room, sender, type, from, to, starred]); // eslint-disable-line

  const roomName = (id: number) => roomById(id)?.name || "گفتگو";
  return (
    <div className="cx-drawer-wrap" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <aside className="cx-drawer">
        <div className="cx-drawer-head"><h3>{starred ? "پیام‌های ستاره‌دار" : "جستجوی پیام‌ها"}</h3><button className="cx-icon-btn" onClick={onClose}><X size={19} /></button></div>
        <div className="cx-drawer-tools">
          <div className="cx-search"><Search size={15} /><input autoFocus value={q} onChange={e => { setQ(e.target.value); setStarred(false); }} placeholder="متن پیام یا نام فایل…" /></div>
          <div className="cx-grid2">
            <select className="cx-input" value={room} onChange={e => { setRoom(+e.target.value); setStarred(false); }}><option value={0}>همه گفتگوها</option>{rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
            <select className="cx-input" value={sender} onChange={e => { setSender(+e.target.value); setStarred(false); }}><option value={0}>همه فرستنده‌ها</option>{users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
          </div>
          <div className="cx-seg sm">
            {[["", "همه"], ["media", "رسانه"], ["file", "فایل"], ["voice", "ویس"], ["image", "عکس"]].map(([k, l]) => <button key={k} className={type === k && !starred ? "on" : ""} onClick={() => { setType(k); setStarred(false); }}>{l}</button>)}
          </div>
          <div className="cx-grid2">
            <JalaliPicker value={from} onChange={setFrom} withTime={false} placeholder="از تاریخ" />
            <JalaliPicker value={to} onChange={setTo} withTime={false} placeholder="تا تاریخ" min={from || undefined} />
          </div>
          <div className="cx-row-gap">
            <button className={`cx-btn sm ${starred ? "primary" : ""}`} onClick={() => setStarred(s => !s)}><Star size={14} /> ستاره‌دارها</button>
            {(from || to || sender || type || room) && <button className="cx-btn sm" onClick={() => { setFrom(null); setTo(null); setSender(0); setType(""); setRoom(0); }}>پاک‌کردن فیلترها</button>}
          </div>
        </div>
        <div className="cx-drawer-body">
          {busy && <div className="cx-center sm"><Spinner /></div>}
          {!busy && res === null && <Empty icon={<Search size={26} />} title="جستجو در همه پیام‌ها" text="حداقل دو حرف بنویسید یا با تاریخ، فرستنده و نوع فیلتر کنید." />}
          {!busy && res?.length === 0 && <Empty title="نتیجه‌ای پیدا نشد" />}
          {!busy && res?.map(m => (
            <button key={m.id} className="cx-hit" onClick={() => { openRoom(m.room_id, { messageId: m.id }); onClose(); }}>
              <Avatar name={m.sender_name} src={m.sender_avatar} size={36} />
              <div><div className="cx-hit-top"><b>{m.sender_name}</b><span>در {roomName(m.room_id)}</span><time>{fmtDateTime(m.created_at)}</time></div>
                <p>{m.type === "voice" ? <><Mic size={13} /> پیام صوتی</> : m.type === "image" ? <><ImageIcon size={13} /> {m.file_name}</> : m.type === "file" ? <><FileText size={13} /> {m.file_name}</> : <Hit text={m.content || ""} q={q.trim()} />}{m.is_deleted ? <em> (حذف‌شده)</em> : null}</p></div>
            </button>
          ))}
        </div>
      </aside>
    </div>
  );
}
